import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  buildConceptStatsRequest,
  fetchConceptStats,
  parseConceptStatsData,
  type ConceptStatsData,
} from "../../../services/autoTaggingService";
import {
  assertDraftComboAvailability,
  collectExceededConceptLimits,
  collectUniqueConceptCombos,
  groupDraftItemsByConceptCombo,
  type ConceptComboDraftItem,
  type ConceptPlanLimitItem,
} from "./conceptStatsValidation";

type SnackbarSeverity = "error" | "warning" | "info" | "success";

/**
 * Keeps concept-stats in sync with draft rows:
 * - fetches stats for each unique Object Type + Sub-Type + Concept combo
 * - exposes helpers to block add / submit / import when counts exceed available slots
 */
export function useDraftConceptStats(
  draftItems: readonly ConceptComboDraftItem[],
  defaultObjectType: string,
  showMessage: (message: string, severity?: SnackbarSeverity) => void,
  onPlanLimitReached?: (
    items: ConceptPlanLimitItem[],
    itemsSnapshot?: readonly ConceptComboDraftItem[],
    toastMessage?: string,
    clearOptions?: {
      onlyItemId?: string;
      availableByComboKey?: Readonly<Record<string, number>>;
    },
  ) => void,
) {
  const [statsByComboKey, setStatsByComboKey] = useState<
    Record<string, ConceptStatsData>
  >({});
  const [loadingComboKeys, setLoadingComboKeys] = useState<Set<string>>(
    () => new Set(),
  );
  const lastExceededSignatureRef = useRef<string | null>(null);
  const statsByComboKeyRef = useRef(statsByComboKey);
  const onPlanLimitReachedRef = useRef(onPlanLimitReached);

  statsByComboKeyRef.current = statsByComboKey;
  onPlanLimitReachedRef.current = onPlanLimitReached;

  const uniqueCombos = useMemo(
    () => collectUniqueConceptCombos(draftItems, defaultObjectType),
    [defaultObjectType, draftItems],
  );

  const comboKeysSignature = useMemo(
    () =>
      [...uniqueCombos.keys()]
        .sort((a, b) => a.localeCompare(b))
        .join("|"),
    [uniqueCombos],
  );

  const draftCountSignature = useMemo(() => {
    const groups = groupDraftItemsByConceptCombo(draftItems, defaultObjectType);
    return [...groups.entries()]
      .sort(([left], [right]) => left.localeCompare(right))
      .map(([key, groupItems]) => `${key}:${groupItems.length}`)
      .join("|");
  }, [defaultObjectType, draftItems]);

  // Fetch stats whenever a new complete combo appears in the draft list.
  useEffect(() => {
    if (uniqueCombos.size === 0) {
      setStatsByComboKey((prev) =>
        Object.keys(prev).length === 0 ? prev : {},
      );
      return undefined;
    }

    let cancelled = false;
    const activeKeys = new Set(uniqueCombos.keys());

    setStatsByComboKey((prev) => {
      const next: Record<string, ConceptStatsData> = {};
      for (const key of activeKeys) {
        if (prev[key]) next[key] = prev[key];
      }
      return next;
    });

    uniqueCombos.forEach((combo, key) => {
      if (statsByComboKeyRef.current[key]) return;

      setLoadingComboKeys((prev) => {
        if (prev.has(key)) return prev;
        const next = new Set(prev);
        next.add(key);
        return next;
      });

      void fetchConceptStats(buildConceptStatsRequest(combo))
        .then((response) => {
          if (cancelled) return;

          const stats = parseConceptStatsData(response);
          if (!stats) {
            const message =
              response.message?.trim() ||
              "Failed to check plan availability.";
            showMessage(message, "warning");
            return;
          }

          setStatsByComboKey((prev) => ({ ...prev, [key]: stats }));
        })
        .catch(() => {
          if (cancelled) return;
          showMessage("Failed to check plan availability.", "warning");
        })
        .finally(() => {
          if (cancelled) return;
          setLoadingComboKeys((prev) => {
            if (!prev.has(key)) return prev;
            const next = new Set(prev);
            next.delete(key);
            return next;
          });
        });
    });

    return () => {
      cancelled = true;
    };
  }, [comboKeysSignature, showMessage, uniqueCombos]);

  // Toast + clear once when loaded stats show the current draft exceeds slots.
  useEffect(() => {
    const exceeded = collectExceededConceptLimits(
      draftItems,
      statsByComboKey,
      defaultObjectType,
    );

    if (exceeded.length === 0) {
      lastExceededSignatureRef.current = null;
      return;
    }

    const notifyKey = `${exceeded
      .map((item) => `${item.conceptCode}:${item.currentCount}/${item.limit}`)
      .join(",")}:${draftCountSignature}`;
    if (lastExceededSignatureRef.current === notifyKey) return;

    lastExceededSignatureRef.current = notifyKey;

    const availableByComboKey: Record<string, number> = {};
    const groups = groupDraftItemsByConceptCombo(draftItems, defaultObjectType);
    for (const key of groups.keys()) {
      const stats = statsByComboKey[key];
      if (stats) {
        availableByComboKey[key] = stats.available;
      }
    }

    onPlanLimitReachedRef.current?.(exceeded, draftItems, undefined, {
      availableByComboKey,
    });
  }, [
    defaultObjectType,
    draftCountSignature,
    draftItems,
    statsByComboKey,
  ]);

  const getExceededLimits = useCallback(
    (items: readonly ConceptComboDraftItem[] = draftItems) =>
      collectExceededConceptLimits(items, statsByComboKey, defaultObjectType),
    [defaultObjectType, draftItems, statsByComboKey],
  );

  const assertAvailability = useCallback(
    async (items: readonly ConceptComboDraftItem[] = draftItems) => {
      await assertDraftComboAvailability(items, defaultObjectType);
    },
    [defaultObjectType, draftItems],
  );

  const isLoading = loadingComboKeys.size > 0;

  return {
    statsByComboKey,
    isLoading,
    getExceededLimits,
    assertAvailability,
  };
}
